import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, NgZone } from "@angular/core";
import {
  Observable,
  Subject,
  Subscription,
  from as observableFrom,
  fromEvent as observableFromEvent,
  merge as observableMerge,
  of as observableOf,
  throwError as observableThrowError
} from "rxjs";
import { filter, finalize, mergeMap } from "rxjs/operators";

import { ElementContainerComponent } from "@lantern/app/common/utils/element-container.component";

import { ElementContainerRegistry } from "../element-container";
import {
  DEFAULT_SCROLL_OFFSET,
  INTERRUPT_KEYS,
  INTERRUPT_MOUSE_EVENTS,
  ScrollOffset
} from "./constants";
import {
  EASING_FUNCTIONS,
  EasingFunction,
  EasingFunctions
} from "./easing-functions";
import {
  ScrollInstruction,
  ScrollInstructionSet
} from "./scroll-instruction-set";

@Injectable()
export class ScrollToService {
  private documentRef: Document;

  /**
   * @internal
   */
  protected requestAnimationFrame: (callback: () => void) => void;

  constructor(
    private elementContainerRegistry: ElementContainerRegistry,
    private ngZone: NgZone,
    @Inject(DOCUMENT) documentRef: any
  ) {
    this.documentRef = documentRef;
    this.requestAnimationFrame = documentRef.defaultView.requestAnimationFrame;
  }

  private currentInstructionSet: ScrollInstructionSet | undefined;
  scrollTo(
    elem: HTMLElement,
    offset: ScrollOffset = DEFAULT_SCROLL_OFFSET,
    duration: number = 200,
    easingFn: EasingFunction | keyof EasingFunctions = "easeOutQuad"
  ): Observable<void> {
    this.stopScroll();

    let subject: Subject<void> = new Subject<void>();
    this.makeElementVisible(elem)
      .pipe(
        mergeMap<void, any>(() => {
          this.currentInstructionSet = this.getScrollInstructionsSet(
            elem,
            offset,
            duration,
            easingFn
          );
          return this.ngZone.runOutsideAngular(() => {
            this.bindInterruptListeners();
            return this.currentInstructionSet!.animate();
          });
        }),
        finalize(() => {
          this.unbindInterruptListeners();
          this.currentInstructionSet = undefined;
        })
      )
      .subscribe(
        () => {
          subject.next();
        },
        (err) => {
          subject.error(err);
        }
      );
    return subject;
  }

  stopScroll(): void {
    this.unbindInterruptListeners();

    if (this.currentInstructionSet == null) {
      return;
    }
    this.currentInstructionSet.stop();
    this.currentInstructionSet = undefined;
  }

  private interruptSubscription?: Subscription;
  private bindInterruptListeners(): void {
    this.unbindInterruptListeners();

    let mouseObservables: Array<
      Observable<MouseEvent>
    > = INTERRUPT_MOUSE_EVENTS.map((name) => {
      return observableFromEvent<MouseEvent>(this.documentRef, name);
    });
    let keyObservable: Observable<KeyboardEvent> = observableFromEvent<
      KeyboardEvent
    >(this.documentRef, "keyup").pipe(
      filter((e) => {
        return INTERRUPT_KEYS.includes(e.key);
      })
    );

    this.interruptSubscription = observableMerge(
      ...mouseObservables,
      keyObservable
    ).subscribe(() => {
      this.stopScroll();
    });
  }
  private unbindInterruptListeners(): void {
    if (this.interruptSubscription == null) {
      return;
    }
    this.interruptSubscription.unsubscribe();
    delete this.interruptSubscription;
  }

  private makeElementVisible(elem: HTMLElement): Observable<void> {
    let hiddenContainers: ElementContainerComponent[] = this.elementContainerRegistry
      .getContainers(elem)
      .filter((container) => {
        return !container.isVisible();
      });
    if (hiddenContainers.length === 0) {
      return observableOf(undefined);
    }

    let allContainersCanBeVisible: boolean = hiddenContainers.every(
      (container) => {
        return container.canBeVisible();
      }
    );
    if (!allContainersCanBeVisible) {
      return observableThrowError(
        new Error("Cannot make scroll to target element visible")
      );
    }

    let promise: Promise<void> = Promise.all(
      hiddenContainers.map((container) => {
        return container.makeVisible();
      })
    ).then(() => {
      return new Promise<void>((resolve) => {
        // use the documentRef's raf
        this.requestAnimationFrame(resolve);
      });
    });

    return observableFrom(promise);
  }

  private getScrollInstructionsSet(
    elem: HTMLElement,
    offset: ScrollOffset,
    duration: number = 200,
    easingFn: EasingFunction | keyof EasingFunctions
  ): ScrollInstructionSet {
    if (typeof easingFn === "string") {
      easingFn = EASING_FUNCTIONS[easingFn];
    }

    offset = Object.assign(
      {
        left: 0,
        top: 0
      },
      offset
    );

    let getComputedStyle: Window["getComputedStyle"] = this.documentRef
      .defaultView!.getComputedStyle;

    let scrollTargets: HTMLElement[] = [elem];
    elem = elem.parentElement as HTMLElement;
    while (elem != null) {
      let styles: CSSStyleDeclaration = getComputedStyle(elem);
      if (
        styles.overflowX === "scroll" ||
        styles.overflowX === "auto" ||
        styles.overflowY === "scroll" ||
        styles.overflowY === "auto"
      ) {
        scrollTargets.push(elem);
      }
      elem = elem.parentElement as HTMLElement;
    }

    let instructions: ScrollInstruction[] = [];
    Array(scrollTargets.length - 1)
      .fill(undefined)
      .map((_, i) => i)
      .forEach((i) => {
        let instruction: ScrollInstruction = new ScrollInstruction(
          scrollTargets[i + 1],
          scrollTargets[i],
          offset.left,
          offset.top
        );

        if (
          instruction.current.left === instruction.target.left &&
          instruction.current.top === instruction.target.top
        ) {
          // no scrolling needed
          return;
        }

        instructions.push(instruction);

        // set offset for next instruction set
        offset.left = Math.max(0, -instruction.target.left!);
        offset.top = Math.max(0, -instruction.target.top!);
      });

    return new ScrollInstructionSet(
      instructions,
      duration,
      easingFn,
      this.requestAnimationFrame,
      this.getTime.bind(this)
    );
  }

  /**
   * @internal
   */
  protected getTime(): number {
    return Date.now();
  }
}
